import { Box } from '@mui/material';
import React, { useMemo } from 'react';
import type { Holder } from '../../data/types';
import { date, money, maskAccount } from '../../lib/format';
import { useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { KeyValueGrid, SectionCard, StatusChip } from '../../ui/primitives';

export default function HolderPaymentsTab({ holder }: { holder: Holder }) {
  const scope = useTenantScope();
  const distributionRows = useMemo(
    () =>
      scope.distributions
        .filter((distribution) => distribution.lines.some((line) => line.holderId === holder.id))
        .map((distribution) => ({
          distribution,
          line: distribution.lines.find((line) => line.holderId === holder.id)!,
        })),
    [scope.distributions, holder.id],
  );
  const callRows = useMemo(
    () =>
      scope.calls
        .filter((call) => call.lines.some((line) => line.holderId === holder.id))
        .map((call) => ({ call, line: call.lines.find((line) => line.holderId === holder.id)! })),
    [scope.calls, holder.id],
  );
  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
      <SectionCard title="Payment instructions">
        <KeyValueGrid
          items={[
            { label: 'Account name', value: holder.bank.accountName },
            {
              label: 'BSB / account',
              value: `${holder.bank.bsb} · ${maskAccount(holder.bank.accountNumber)}`,
            },
            {
              label: 'Verified',
              value: (
                <StatusChip
                  status={holder.bank.verified ? 'VERIFIED' : 'PENDING'}
                  label={holder.bank.verified ? 'Verified' : 'Not verified — payments held'}
                />
              ),
            },
            {
              label: 'Distribution reinvestment',
              value: holder.drpElection ? 'Reinvest (where offered)' : 'Pay to bank',
            },
          ]}
        />
      </SectionCard>
      <SectionCard title="Withholding">
        <KeyValueGrid
          items={[
            {
              label: 'TFN withholding',
              value:
                holder.tfnStatus === 'NOT_QUOTED' && holder.residency === 'AU'
                  ? 'Applies at 47%'
                  : 'Not applicable',
            },
            {
              label: 'Non-resident withholding',
              value: holder.residency !== 'AU' ? '10% on interest component' : 'Not applicable',
            },
          ]}
        />
      </SectionCard>
      <SectionCard title="Distributions" noPadding sx={{ gridColumn: { lg: '1 / -1' } }}>
        <DataTable
          rows={distributionRows}
          rowKey={(row) => row.distribution.id}
          dense
          defaultSort={{ key: 'date', direction: 'desc' }}
          emptyMessage="No distributions yet"
          columns={[
            {
              key: 'fund',
              label: 'Fund',
              render: (row) =>
                `${scope.fundById.get(row.distribution.fundId)?.code} · ${scope.classById.get(row.distribution.classId)?.code}`,
            },
            {
              key: 'period',
              label: 'Period ending',
              render: (row) => date(row.distribution.periodEnd),
            },
            {
              key: 'date',
              label: 'Payment date',
              sortValue: (row) => row.distribution.paymentDate,
              render: (row) => date(row.distribution.paymentDate),
            },
            {
              key: 'gross',
              label: 'Gross',
              align: 'right',
              render: (row) => money(row.line.gross, true),
            },
            {
              key: 'wht',
              label: 'Withheld',
              align: 'right',
              render: (row) => money(row.line.tfnWithholding + row.line.nrWithholding, true),
            },
            {
              key: 'net',
              label: 'Net',
              align: 'right',
              render: (row) => money(row.line.net, true),
            },
            { key: 'method', label: 'Method', render: (row) => row.line.method },
            {
              key: 'status',
              label: 'Status',
              render: (row) => <StatusChip status={row.distribution.status} />,
            },
          ]}
        />
      </SectionCard>
      {callRows.length > 0 && (
        <SectionCard title="Capital calls" noPadding sx={{ gridColumn: { lg: '1 / -1' } }}>
          <DataTable
            rows={callRows}
            rowKey={(row) => row.call.id}
            dense
            columns={[
              {
                key: 'call',
                label: 'Call',
                render: (row) =>
                  `${scope.fundById.get(row.call.fundId)?.code} Call ${row.call.number} (${row.call.callPct}%)`,
              },
              { key: 'due', label: 'Due', render: (row) => date(row.call.dueDate) },
              {
                key: 'amount',
                label: 'Amount',
                align: 'right',
                render: (row) => money(row.line.amount),
              },
              { key: 'paid', label: 'Paid', align: 'right', render: (row) => money(row.line.paid) },
              { key: 'crn', label: 'BPAY ref', render: (row) => row.line.crn },
              {
                key: 'status',
                label: 'Status',
                render: (row) => <StatusChip status={row.line.status} />,
              },
            ]}
          />
        </SectionCard>
      )}
    </Box>
  );
}
